import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HotelIcon from '@mui/icons-material/Hotel';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useRef, useState } from 'react';
import { formatDate } from '../utils';
import { NumericField } from './NumericField';

/** One row in the search field's live suggestion dropdown — mirrors the backend's PropertySuggestionDto. */
export interface PropertySuggestion {
  id: number;
  name: string;
  subtitle: string;
}

export interface OtaSearchValue {
  /** Raw text in the combined "city, area or property" box. */
  query: string;
  /** Set only when the guest picked a specific hotel from the suggestion dropdown — takes
   *  precedence over `query` on the backend. Cleared as soon as they edit the text further. */
  propertyId?: number;
  checkIn: string;
  checkOut: string;
  guests: number;
}

interface OtaSearchBarProps {
  initial?: Partial<OtaSearchValue>;
  onSearch: (value: OtaSearchValue) => void;
  variant?: 'hero' | 'compact';
  /**
   * Called (debounced, 2+ characters) as the guest types, to fetch live
   * suggestions for the dropdown. Omit to fall back to a plain text field
   * with no suggestions.
   */
  onSuggest?: (q: string) => Promise<PropertySuggestion[]>;
}

export function OtaSearchBar({ initial, onSearch, variant = 'hero', onSuggest }: OtaSearchBarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isHero = variant === 'hero';

  const [query, setQuery] = useState(initial?.query ?? '');
  const [propertyId, setPropertyId] = useState<number | undefined>(initial?.propertyId);
  const [checkIn, setCheckIn] = useState(initial?.checkIn ?? '');
  const [checkOut, setCheckOut] = useState(initial?.checkOut ?? '');
  const [guests, setGuests] = useState(initial?.guests ?? 2);
  const [dateError, setDateError] = useState('');
  const checkInRef = useRef<HTMLInputElement>(null);

  const handleQueryChange = (v: string) => {
    setQuery(v);
    // Any manual edit after picking a specific hotel means the guest is no
    // longer searching for exactly that hotel — fall back to a text search.
    setPropertyId(undefined);
  };

  const handleSelectSuggestion = (s: PropertySuggestion) => {
    setQuery(s.name);
    setPropertyId(s.id);
    // Small MMT-style polish: move straight to picking dates next.
    checkInRef.current?.focus();
  };

  const handleCheckInChange = (v: string) => {
    setCheckIn(v);
    if (checkOut && v && v >= checkOut) {
      setDateError('Check-out date must be after check-in date.');
    } else {
      setDateError('');
    }
  };

  const handleCheckOutChange = (v: string) => {
    setCheckOut(v);
    if (checkIn && v && v <= checkIn) {
      setDateError('Check-out date must be after check-in date.');
    } else {
      setDateError('');
    }
  };

  const submit = () => {
    if (checkIn && checkOut && checkOut <= checkIn) {
      setDateError('Check-out date must be after check-in date.');
      return;
    }
    setDateError('');
    onSearch({ query, propertyId, checkIn, checkOut, guests });
  };

  return (
    <Paper
      elevation={isHero ? 12 : 1}
      sx={{
        p: { xs: 2, md: isHero ? 2.5 : 2 },
        borderRadius: isHero ? 4 : 2,
        background: '#FFFFFF',
        border: isHero ? 'none' : (t) => `1px solid ${t.palette.divider}`,
        boxShadow: isHero
          ? '0 24px 60px rgba(5, 60, 113, 0.18), 0 4px 12px rgba(15, 23, 42, 0.08)'
          : undefined,
      }}
    >
      <Grid container spacing={{ xs: 1.5, md: 1 }} alignItems="center">
        <Grid item xs={12} sm={6} md={4}>
          <CombinedSearchField
            value={query}
            onChange={handleQueryChange}
            onSelectSuggestion={handleSelectSuggestion}
            onSuggest={onSuggest}
          />
        </Grid>
        <DividerCell />
        <Grid item xs={6} sm={4} md={2}>
          <DatePickerField
            label="Check-in"
            icon={<CalendarMonthIcon sx={{ color: 'primary.main' }} />}
            value={checkIn}
            onChange={handleCheckInChange}
            inputRef={checkInRef}
          />
        </Grid>
        <DividerCell />
        <Grid item xs={6} sm={4} md={2}>
          <DatePickerField
            label="Check-out"
            icon={<CalendarMonthIcon sx={{ color: 'primary.main' }} />}
            value={checkOut}
            onChange={handleCheckOutChange}
          />
        </Grid>
        <DividerCell />
        <Grid item xs={6} sm={4} md={1.2}>
          <Box sx={{ px: 1, py: 0.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: -0.25 }}>
              <PersonIcon sx={{ color: 'primary.main' }} />
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}
              >
                Guests
              </Typography>
            </Stack>
            <NumericField
              value={String(guests)}
              onChange={(e) => setGuests(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
              variant="standard"
              fullWidth
              inputProps={{ maxLength: 2 }}
              InputProps={{
                disableUnderline: true,
                sx: { fontSize: '1rem', fontWeight: 500, mt: 0.25 },
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md="auto" sx={{ pl: { md: 1 } }}>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={submit}
            startIcon={<SearchIcon />}
            fullWidth={isMobile}
            sx={{
              height: 64,
              minWidth: { md: 140 },
              px: { md: 4 },
              fontSize: '1rem',
              borderRadius: 2.5,
              whiteSpace: 'nowrap',
            }}
          >
            Search
          </Button>
        </Grid>
      </Grid>
      {dateError && (
        <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
          {dateError}
        </Alert>
      )}
    </Paper>
  );
}

interface CombinedSearchFieldProps {
  value: string;
  onChange: (v: string) => void;
  onSelectSuggestion: (s: PropertySuggestion) => void;
  onSuggest?: (q: string) => Promise<PropertySuggestion[]>;
}

/**
 * The MMT-style "City, area or property" box: a single free-text field that,
 * once the guest has typed 2+ characters, shows a debounced dropdown of
 * matching hotels (icon + name + "Hotel in {area}, {city}" subtitle).
 * Picking one calls {@code onSelectSuggestion}; typing without picking is a
 * plain text search on submit (handled by the parent).
 */
function CombinedSearchField({ value, onChange, onSelectSuggestion, onSuggest }: CombinedSearchFieldProps) {
  const [options, setOptions] = useState<PropertySuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const handleInputChange = (newValue: string) => {
    onChange(newValue);
    if (!onSuggest) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (newValue.trim().length < 2) {
      setOptions([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      onSuggest(newValue.trim())
        .then(setOptions)
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    }, 300);
  };

  return (
    <Box sx={{ px: 1, py: 0.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: -0.25 }}>
        <LocationOnIcon sx={{ color: 'primary.main' }} />
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}
        >
          City, area or property
        </Typography>
      </Stack>
      <Autocomplete<PropertySuggestion, false, false, true>
        freeSolo
        options={options}
        loading={loading}
        filterOptions={(x) => x}
        inputValue={value}
        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.name)}
        isOptionEqualToValue={(opt, val) => (typeof val === 'string' ? opt.name === val : opt.id === val.id)}
        onInputChange={(_, newValue, reason) => {
          // 'input' = the guest typing; 'clear' = the × adornment button.
          // Both need to update the parent's controlled `value` — otherwise the
          // clear button resets MUI's internal text but our controlled
          // `inputValue={value}` prop snaps it right back on the next render.
          if (reason === 'input' || reason === 'clear') handleInputChange(newValue);
        }}
        onChange={(_, newValue) => {
          if (newValue && typeof newValue !== 'string') onSelectSuggestion(newValue);
        }}
        renderOption={(props, option) => {
          // MUI 5.15+ includes `key` inside `props`; spreading it directly into JSX
          // trips React 18.3's "key prop being spread" warning, so pull it out first.
          const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & { key?: React.Key };
          return (
            <Box component="li" key={option.id ?? key} {...optionProps}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 0.25 }}>
                <HotelIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.3 }} />
                <Stack spacing={0}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{option.subtitle}</Typography>
                </Stack>
              </Stack>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Where to? Hotel name, area, or landmark"
            variant="standard"
            fullWidth
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              sx: { fontSize: '1rem', fontWeight: 500, mt: 0.25 },
              endAdornment: loading ? <CircularProgress size={16} /> : params.InputProps.endAdornment,
            }}
          />
        )}
      />
    </Box>
  );
}

interface DatePickerFieldProps {
  label: string;
  icon: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  inputRef?: React.Ref<HTMLInputElement>;
}

function DatePickerField({ label, icon, value, onChange, inputRef }: DatePickerFieldProps) {
  const display = value ? formatDate(value) : '';

  return (
    <Box sx={{ px: 1, py: 0.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
        {icon}
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}
        >
          {label}
        </Typography>
      </Stack>
      <Box sx={{ position: 'relative', width: '100%', cursor: 'pointer' }}>
        <Typography
          variant="body1"
          sx={{
            fontSize: '1rem',
            fontWeight: value ? 700 : 400,
            lineHeight: 1.75,
            color: value ? 'text.primary' : 'text.disabled',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        >
          {display || 'dd/mmm/yyyy'}
        </Typography>
        <Box
          component="input"
          type="date"
          ref={inputRef}
          value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0,
            width: '100%',
            height: '100%',
            cursor: 'pointer',
            border: 'none',
            background: 'transparent',
            fontSize: '1rem',
            '&::-webkit-calendar-picker-indicator': {
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              opacity: 0,
              cursor: 'pointer',
            },
          }}
        />
      </Box>
    </Box>
  );
}

function DividerCell() {
  return (
    <Grid item md="auto" sx={{ display: { xs: 'none', md: 'block' } }}>
      <Box sx={{ width: '1px', height: 44, bgcolor: 'divider' }} />
    </Grid>
  );
}
